import PropTypes from 'prop-types';
import React from 'react';
import { KeyBindingUtil, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './GeckoEditor.scss';
import { Keys } from './modules/KeyBindings.js';
import { handleNewLine } from 'draftjs-utils';
import { CONSTS } from 'constants/index.js';
import AttachmentPreview from 'routes/conversations/components/AttachmentPreview';

const GeckoEditor = (props) => {
  const editorKeyBindingFn = (e) => {
    props.onKeyPress(e);
    return Keys.handleKey(e, props);
  };

  const handlePastedFiles = (files) => {
    props.handlePastedFiles(files);
    return 'handled';
  };

  const handlePastedText = (text, html, editorState) => {
    if (html && html.includes('<img')) {
      props.handlePastedText(text);
      return true;
    } else {
      return false;
    }
  };

  const handleReturn = (e) => {
    if (
      KeyBindingUtil.isCtrlKeyCommand(e) ||
      KeyBindingUtil.hasCommandModifier(e)
    ) {
      e.preventDefault();
      props.submitEditorContent();
      return true;
    }
    const editorState = handleNewLine(props.editorState, e);
    if (editorState) {
      props.onEditorStateChange(editorState);
      return true;
    }
    return false;
  };

  const getPlaceholder = () => {
    if (
      convertToRaw(props.editorState.getCurrentContent()).blocks[0].type !==
      'unstyled'
    ) {
      return '';
    }
    return props.noteMode.on ? 'Type an internal note...' : 'Type a message...';
  };

  const draftEditor = () => {
    const createStrategy = (tag) => (contentBlock, callback, contentState) => {
      return contentBlock.findEntityRanges((char) => {
        const entityKey = char.getEntity();
        return (
          entityKey !== null &&
          contentState.getEntity(entityKey).getType() === tag
        );
      }, callback);
    };
    const decorators = [
      {
        strategy: createStrategy('ATTACHMENT'),
        component: (props) => {
          const attachment = props.contentState
            .getEntity(props.entityKey)
            .getData();
          return (
            <div contentEditable={false}>
              <AttachmentPreview
                attachment={attachment}
                onClick={() => {}}
                width={200}
              />
            </div>
          );
        },
      },
      {
        strategy: createStrategy('VIDEO'),
        component: (props) => {
          const entityData = props.contentState
            .getEntity(props.entityKey)
            .getData();
          return (
            <video
              src={entityData.src}
              controls={entityData.controls}
              style={{ maxWidth: '100%' }}
            />
          );
        },
      },
      {
        strategy: createStrategy('AUDIO'),
        component: (props) => {
          const entityData = props.contentState
            .getEntity(props.entityKey)
            .getData();
          return (
            <audio
              src={entityData.src}
              controls={entityData.controls}
              style={{ maxWidth: '100%' }}
            />
          );
        },
      },
    ];
    if (props.attachments.length > 0) {
      return null;
    }
    return (
      <div className="gecko-editor" ref={(elem) => props.setEditorDiv(elem)}>
        <Editor
          customDecorators={decorators.concat(props.additionalDecorators)}
          editorClassName={
            props.isEditorMaximised ? 'editor maximised' : 'editor'
          }
          editorState={props.editorState}
          keyBindingFn={editorKeyBindingFn}
          handleReturn={handleReturn}
          handlePastedFiles={handlePastedFiles}
          handlePastedText={handlePastedText}
          ref={(elem) => props.setEditorRef(elem)}
          wrapperClassName="demo-wrapper"
          onEditorStateChange={props.onEditorStateChange}
          placeholder={props.placeholder || getPlaceholder()}
          spellCheck={true}
          style={{}}
          toolbarClassName={props.toolbarClassName}
          toolbarHidden={
            props.editorMode === CONSTS.EDITOR_MODE_TYPE.PLAIN ||
            !props.highlightedText
          }
          toolbar={{
            options: ['inline', 'list', 'link'],
            inline: {
              options: ['bold', 'italic', 'underline', 'strikethrough'],
              bold: { className: 'bold' },
              italic: { className: 'italic' },
              underline: { className: 'underline' },
              strikethrough: { className: 'strikethrough' },
            },
            list: {
              unordered: { className: 'unordered-list' },
              ordered: { className: 'ordered-list' },
              indent: { className: 'indent' },
              outdent: { className: 'outdent' },
            },
            link: {
              link: { className: 'linkify', target: 'heyo' },
              unlink: { className: 'delinkify' },
              showOpenOptionOnHover: false,
              defaultTargetOption: '_blank',
            },
            image: {
              alignmentEnabled: false,
            },
          }}
        />
      </div>
    );
  };
  return draftEditor();
};

GeckoEditor.defaultProps = {
  additionalDecorators: [],
};

GeckoEditor.propTypes = {
  additionalDecorators: PropTypes.array,
  attachments: PropTypes.array,
  clearBlockAndSetType: PropTypes.func,
  editorMode: PropTypes.string,
  editorState: PropTypes.object,
  handlePastedFiles: PropTypes.func,
  handlePastedText: PropTypes.func,
  highlightedText: PropTypes.bool,
  noteMode: PropTypes.object,
  onEditorStateChange: PropTypes.func,
  onKeyPress: PropTypes.func,
  setEditorDiv: PropTypes.func,
  setEditorRef: PropTypes.func,
  submitEditorContent: PropTypes.func,
  toggleList: PropTypes.func,
  toolbarClassName: PropTypes.string,
};

export default GeckoEditor;
